import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Radio,
  Pagination,
  CircularProgress,
  IconButton,
  TextField,
  Button,
  Drawer,
  Alert,
} from "@mui/material";
import "@fontsource/manrope";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import ToolMapping from "./ToolMapping";
import { getCaseTemplatesFromTestRail } from "../../services/webServices"; // Import the new function

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const ProjectsComponentPage = ({
  taskType,
  issueType,
  projectsLoading,
  projectsList,
  selectedProject,
  setSelectedProject,
  onClose,
  onProjectSelect,
  connectionData,
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [toolMappingOpen, setToolMappingOpen] = useState(false);
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState(null);

  const handleRadioChange = (project) => {
    setSelectedProject(project);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSetMapping = async () => {
    if (!selectedProject) return;

    // For TestRail, open template selection drawer
    if (taskType === 'TestRail') {
      await fetchTemplates(selectedProject.id);
    } else {
      // For other tools, proceed directly to tool mapping
      setToolMappingOpen(true);
    }
  };

  const fetchTemplates = async (projectId) => {
    setLoadingTemplates(true);
    setTemplateError(null);
    
    try {
      const response = await getCaseTemplatesFromTestRail(projectId, connectionData.config_id);
      const templatesData = response.data || [];
      
      if (Array.isArray(templatesData) && templatesData.length > 0) {
        setTemplates(templatesData);
        setTemplateDrawerOpen(true);
      } else {
        setTemplateError("No templates found for this TestRail project");
        // If no templates, proceed directly to tool mapping
        setTimeout(() => {
          setToolMappingOpen(true);
        }, 2000);
      }
    } catch (error) {
      console.error('Error fetching TestRail templates:', error);
      setTemplateError("Failed to load templates. Please try again.");
      // On error, proceed directly to tool mapping
      setTimeout(() => {
        setToolMappingOpen(true);
      }, 2000);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirmTemplate = () => {
    if (selectedTemplate) {
      setTemplateDrawerOpen(false);
      setToolMappingOpen(true);
    }
  };

  const handleCloseTemplateDrawer = () => {
    setTemplateDrawerOpen(false);
    setToolMappingOpen(true);
  };

  const handleCloseToolMapping = () => {
    setToolMappingOpen(false);
  };

  // Enhanced project data normalization to handle all tool formats
  const normalizeProjectData = (projects, toolType) => {
    if (!Array.isArray(projects)) {
      if (projects && typeof projects === 'object') {
        if (projects.data && Array.isArray(projects.data)) {
          projects = projects.data;
        } else if (projects.value && Array.isArray(projects.value)) {
          projects = projects.value;
        } else if (projects.results && Array.isArray(projects.results)) {
          projects = projects.results;
        } else if (Array.isArray(projects)) {
          // Already an array, use as is
        } else {
          projects = [projects];
        }
      } else {
        return [];
      }
    }

    return projects.map((project) => {
      const baseProject = {
        id: project.id || project.key || project.projectId,
        name: project.name || project.projectName,
        key: project.key || project.spaceKey,
        type: toolType?.toLowerCase() || 'unknown',
        originalData: project,
      };

      switch (toolType?.toLowerCase()) {
        case 'jira':
          return {
            ...baseProject,
            displayName: `${project.name}`,
            toolSpecific: {
              id: project.id,
              key: project.key,
            }
          };
        
        case 'azure':
        case 'ado':
          return {
            ...baseProject,
            displayName: project.name,
            toolSpecific: {
              visibility: project.visibility,
              lastUpdateTime: project.lastUpdateTime,
            }
          };
        
        case 'qtest':
          return {
            ...baseProject,
            displayName: project.name,
            toolSpecific: {
              id: project.id,
            }
          };
        
        case 'testrail':
          return {
            ...baseProject,
            displayName: project.name,
            toolSpecific: {
              announcement: project.announcement,
              is_completed: project.is_completed,
            }
          };
        
        case 'confluence':
          return {
            ...baseProject,
            displayName: `${project.name} (${project.key || project.spaceKey})`,
            toolSpecific: {
              type: project.type,
              status: project.status,
            }
          };
        
        default:
          return {
            ...baseProject,
            displayName: project.name || project.projectName || `Project ${baseProject.id}`,
          };
      }
    });
  };

  const normalizedProjects = normalizeProjectData(projectsList, taskType);
  const projectsPerPage = 6;

  const filteredProjects = normalizedProjects.filter((project) =>
    project.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedProjects = filteredProjects.slice(
    (page - 1) * projectsPerPage,
    page * projectsPerPage
  );

  // Reset selected project when projects list changes
  useEffect(() => {
    setSelectedProject(null);
    setPage(1);
  }, [projectsList, setSelectedProject]);

  // Template Selection Component
  const TemplateSelectionDrawer = () => (
    <Drawer
      anchor="right"
      open={templateDrawerOpen}
      onClose={handleCloseTemplateDrawer}
      sx={{
        "& .MuiDrawer-paper": {
          width: 500,
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: "center" }}>
            Select TestRail Template
          </Typography>
          <IconButton onClick={handleCloseTemplateDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {templateError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {templateError}
          </Alert>
        )}

        {loadingTemplates ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading templates...
            </Typography>
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, flexDirection: "column" }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No templates available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This project doesn't have any case templates.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 1,
                flex: 1,
                overflowY: "auto",
                mb: 2,
              }}
            >
              {templates.map((template, index) => (
                <Card
                  key={`${template.id}-${index}`}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "90%",
                    p: 2,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: selectedTemplate?.id === template.id ? "2px solid #49A3FF" : "1px solid #e0e0e0",
                    "&:hover": {
                      borderColor: selectedTemplate?.id === template.id ? "#49A3FF" : "#ccc",
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <Radio
                    checked={selectedTemplate?.id === template.id}
                    onChange={() => handleTemplateSelect(template)}
                    value={template}
                    name="template-radio"
                    size="small"
                  />
                  <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", textAlign: "left" }}>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Template ID: {template.id}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="contained"
                onClick={handleConfirmTemplate}
                disabled={!selectedTemplate}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#49A3FF",
                  "&:hover": { bgcolor: "#369bff" },
                  "&.Mui-disabled": { bgcolor: "#f5f5f5", color: "#ccc" },
                  textTransform: "none",
                  px: 3,
                }}
              >
                Confirm Template
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
          <TextField
            size="small"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ width: "100%" }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "#888", mr: 1 }} />,
            }}
          />
        </Box>

        {projectsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading {taskType} projects...
            </Typography>
          </Box>
        ) : normalizedProjects.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, flexDirection: "column" }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {projectsList && projectsList.length === 0 
                ? `No projects available in the selected ${taskType} connection.`
                : "Could not load projects. Please check your connection."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, flex: 1, overflowY: "auto", mb: 2 }}>
              {displayedProjects.map((project, index) => (
                <Card
                  key={`${project.id}-${index}-${project.type}`}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "90%",
                    height: "90px",
                    p: 1,
                    borderRadius: 2,
                    gap: 0.5,
                    cursor: "pointer",
                    border: selectedProject?.id === project.id ? "2px solid #49A3FF" : "1px solid #e0e0e0",
                    "&:hover": {
                      borderColor: selectedProject?.id === project.id ? "#49A3FF" : "#ccc",
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => handleRadioChange(project)}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                    <Radio
                      checked={selectedProject?.id === project.id}
                      onChange={() => handleRadioChange(project)}
                      value={project}
                      name="project-radio"
                      size="small"
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.4 }}>
                        {project.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {project.type} project
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>

            {filteredProjects.length > projectsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Pagination
                  count={Math.ceil(filteredProjects.length / projectsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button sx={{ color: "#666", textTransform: "none", borderColor: "#ddd" }} variant="outlined" onClick={onClose}>
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSetMapping}
                disabled={!selectedProject}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#49A3FF",
                  "&:hover": { bgcolor: "#369bff" },
                  "&.Mui-disabled": { bgcolor: "#f5f5f5", color: "#ccc" },
                  textTransform: "none",
                  px: 3,
                }}
              >
                Next
              </Button>
            </Box>
          </>
        )}

        {/* Template Selection Drawer */}
        <TemplateSelectionDrawer />

        <ToolMapping
          open={toolMappingOpen}
          onClose={handleCloseToolMapping}
          onCloseAll={() => {
            setToolMappingOpen(false);
            onClose();
          }}
          selectedApp={taskType}
          selectedConnection={connectionData?.config_id}
          selectedProject={selectedProject}
          selectedTemplate={selectedTemplate} // Pass the selected template to ToolMapping
          connectionData={connectionData}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ProjectsComponentPage;